import { useState, useEffect } from 'react'
import { supabase } from './supabaseClient'
import { calculateSeoScore, slugify } from './seoUtils'

export default function BlogEditor({ blogId, onNavigate, addToast }) {
  const isNew = !blogId
  const [loading, setLoading] = useState(!isNew)
  const [saving, setSaving] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [categories, setCategories] = useState([])

  const [blog, setBlog] = useState({
    title: '', slug: '', content: '', excerpt: '',
    meta_title: '', meta_description: '', focus_keyword: '',
    tags: [], category_id: '', status: 'draft', ai_generated: false,
    featured_image: '', scheduled_at: '', photographer_credit: '',
    content_type: 'standalone', is_pillar: false, pillar_id: '', cluster_name: '',
  })

  const [seo, setSeo] = useState({ score: 0, checks: [], words: 0 })
  const [aiTopic, setAiTopic] = useState('')
  const [aiKeyword, setAiKeyword] = useState('')
  const [uploadingImage, setUploadingImage] = useState(false)
  const [pillars, setPillars] = useState([])

  useEffect(() => {
    supabase.from('categories').select('id,name').order('name').then(({ data, error }) => {
      if (error) console.error('Categories load error:', error)
      setCategories(data || [])
    })
    supabase.from('blogs').select('id,title,slug').eq('is_pillar', true).eq('status', 'published').order('title').then(({ data }) => {
      setPillars(data || [])
    })
    if (!isNew) loadBlog()
  }, [blogId])

  useEffect(() => {
    setSeo(calculateSeoScore(blog))
  }, [blog.title, blog.content, blog.meta_description, blog.meta_title, blog.focus_keyword, blog.slug])

  async function loadBlog() {
    const { data } = await supabase.from('blogs').select('*').eq('id', blogId).single()
    if (data) {
      setBlog({ ...data, tags: data.tags || [], scheduled_at: data.scheduled_at || '' })
      setAiKeyword(data.focus_keyword || '')
    }
    setLoading(false)
  }

  function update(field, value) {
    setBlog(prev => {
      const next = { ...prev, [field]: value }
      if (field === 'title' && isNew) next.slug = slugify(value)
      if (field === 'title' && !next.meta_title) next.meta_title = value
      return next
    })
  }

  function insertAtCursor(textarea, text) {
    if (!textarea) return
    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const newContent = blog.content.substring(0, start) + text + blog.content.substring(end)
    update('content', newContent)
    setTimeout(() => {
      textarea.selectionStart = textarea.selectionEnd = start + text.length
      textarea.focus()
    }, 0)
  }

  function toolbarAction(action) {
    const ta = document.getElementById('main-editor')
    const sel = ta ? ta.value.substring(ta.selectionStart, ta.selectionEnd) : ''
    const map = {
      bold:  () => insertAtCursor(ta, `**${sel || 'bold text'}**`),
      italic:() => insertAtCursor(ta, `*${sel || 'italic text'}*`),
      h2:    () => insertAtCursor(ta, `\n## ${sel || 'Heading'}\n`),
      h3:    () => insertAtCursor(ta, `\n### ${sel || 'Subheading'}\n`),
      link:  () => insertAtCursor(ta, `[${sel || 'link text'}](url)`),
      ul:    () => insertAtCursor(ta, `\n- ${sel || 'list item'}\n`),
      ol:    () => insertAtCursor(ta, `\n1. ${sel || 'list item'}\n`),
      quote: () => insertAtCursor(ta, `\n> ${sel || 'blockquote'}\n`),
      faq:   () => insertAtCursor(ta, `\n## Frequently Asked Questions\n\n### Q: Question here?\nAnswer here.\n\n### Q: Another question?\nAnswer here.\n`),
      ilink: () => insertAtCursor(ta, `[LINK: related blog topic]`),
    }
    if (map[action]) map[action]()
  }

  async function generateWithAI() {
    if (!aiTopic && !aiKeyword) { addToast('Enter a topic or keyword first', 'error'); return }
    setGenerating(true)
    const keyword = aiKeyword || aiTopic
    try {
      // Calls /api/generate — a secure Vercel serverless function
      // API key lives on the server, never exposed to the browser
      const { data: { session: genSession } } = await supabase.auth.getSession()
      const selectedPillar = blog.pillar_id ? pillars.find(p => p.id === blog.pillar_id) : null
      const res = await fetch('/api/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${genSession?.access_token}`,
        },
        body: JSON.stringify({
          keyword,
          topic: aiTopic,
          content_type: blog.content_type || 'standalone',
          pillarContext: selectedPillar ? { title: selectedPillar.title, slug: selectedPillar.slug } : null,
        }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || `Server error ${res.status}`)
      const generated = data.text || ''
      if (!generated) throw new Error('Empty response from AI')

      const titleMatch = generated.match(/^#\s+(.+)$/m)
      const generatedTitle = titleMatch ? titleMatch[1].trim() : aiTopic || keyword

      // Remove the H1 title line from body
      const body = generated.replace(/^#\s+.+$/m, '').trim()

      // Extract first real paragraph (skip any lines starting with # or **)
      const paragraphs = body.split('\n').filter(l => l.trim() && !l.trim().startsWith('#') && !l.trim().startsWith('**') && !l.trim().startsWith('-') && !l.trim().startsWith('>'))
      const firstPara = paragraphs[0] || ''

      // Build excerpt: first paragraph trimmed to ~200 chars, ending at a word boundary
      let excerpt = firstPara.replace(/[*_`]/g, '').trim()
      if (excerpt.length > 220) {
        excerpt = excerpt.substring(0, 220).replace(/\s+\S*$/, '') + '…'
      }

      // Build meta description: keyword + excerpt trimmed to 150 chars
      const metaBase = excerpt.length > 10 ? excerpt : `Everything you need to know about ${keyword} for your local business.`
      let metaDescription = metaBase
      if (metaDescription.length > 155) {
        metaDescription = metaDescription.substring(0, 152).replace(/\s+\S*$/, '') + '…'
      }
      // Ensure keyword appears in meta description
      if (keyword && !metaDescription.toLowerCase().includes(keyword.toLowerCase())) {
        const shortened = metaDescription.substring(0, 155 - keyword.length - 3)
        metaDescription = `${keyword} — ${shortened}`.substring(0, 155)
      }

      // Build meta title (50-60 chars with keyword)
      let metaTitle = generatedTitle
      if (metaTitle.length > 60) {
        metaTitle = metaTitle.substring(0, 57).replace(/\s+\S*$/, '') + '…'
      }
      if (metaTitle.length < 50 && !metaTitle.toLowerCase().includes(keyword.toLowerCase())) {
        metaTitle = `${metaTitle} | Cobro`.substring(0, 60)
      }

      // Auto-generate tags from keyword
      const autoTags = keyword.split(/\s+/).filter(w => w.length > 3)
      autoTags.push('cobro', 'google reviews')

      setBlog(prev => ({
        ...prev,
        title: generatedTitle,
        slug: slugify(generatedTitle),
        content: body,
        focus_keyword: keyword,
        ai_generated: true,
        excerpt,
        meta_title: metaTitle,
        meta_description: metaDescription,
        tags: [...new Set(autoTags)],
        // Auto-set featured image from Unsplash if returned and not already set
        featured_image:       prev.featured_image || data.featuredImage || '',
        photographer_credit:  prev.photographer_credit || data.photographerCredit || '',
      }))
      addToast('Blog generated successfully!')
    } catch (err) {
      console.error('AI generation error:', err)
      addToast('Generation failed: ' + (err.message || 'Unknown error'), 'error')
    }
    setGenerating(false)
  }

  async function handleImageUpload(e) {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.type.startsWith('image/')) { addToast('Please select an image file', 'error'); return }
    if (file.size > 5 * 1024 * 1024) { addToast('Image must be under 5MB', 'error'); return }

    setUploadingImage(true)
    try {
      const ext = file.name.split('.').pop()
      const filename = `${Date.now()}-${Math.random().toString(36).substring(2)}.${ext}`
      const { data, error } = await supabase.storage
        .from('blog-images')
        .upload(filename, file, { contentType: file.type, upsert: false })

      if (error) throw new Error(error.message)

      const { data: urlData } = supabase.storage.from('blog-images').getPublicUrl(filename)
      update('featured_image', urlData.publicUrl)
      addToast('Image uploaded successfully!')
    } catch (err) {
      addToast('Upload failed: ' + err.message, 'error')
    }
    setUploadingImage(false)
  }

  async function saveBlog(status) {
    setSaving(true)
    const payload = {
      ...blog,
      status: status || blog.status,
      seo_score: seo.score,
      tags: typeof blog.tags === 'string'
        ? blog.tags.split(',').map(t => t.trim()).filter(Boolean)
        : blog.tags,
      // Convert empty strings → null for UUID + optional fields
      category_id:      blog.category_id      || null,
      featured_image:   blog.featured_image   || null,
      scheduled_at:     blog.scheduled_at     || null,
      excerpt:          blog.excerpt          || null,
      meta_title:       blog.meta_title       || blog.title || null,
      meta_description: blog.meta_description || null,
      focus_keyword:       blog.focus_keyword       || null,
      photographer_credit: blog.photographer_credit || null,
      content_type:        blog.content_type        || 'standalone',
      is_pillar:           blog.content_type        === 'pillar',
      pillar_id:           blog.pillar_id           || null,
      cluster_name:        blog.cluster_name        || null,
    }
    if (status === 'published' && !blog.published_at) {
      payload.published_at = new Date().toISOString()
    }
    delete payload.id

    let error
    if (isNew) {
      const { error: e } = await supabase.from('blogs').insert([payload])
      error = e
    } else {
      const { error: e } = await supabase.from('blogs').update(payload).eq('id', blogId)
      error = e
    }

    if (error) {
      addToast('Failed to save: ' + error.message, 'error')
    } else {
      addToast(status === 'published' ? 'Blog published! 🎉' : 'Draft saved')
      // Mark keyword as published when blog goes live — non-blocking
      if (status === 'published' && blog.focus_keyword) {
        supabase.from('keywords')
          .update({ status: 'published' })
          .eq('keyword', blog.focus_keyword)
          .then(() => {})
      }
      // Fire publish hooks (indexing + sitemap + n8n) — non-blocking
      if (status === 'published') {
        supabase.auth.getSession().then(({ data: { session: pubSession } }) => {
          fetch('/api/publish-hooks', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${pubSession?.access_token}`,
            },
            body: JSON.stringify({ slug: payload.slug, title: payload.title }),
          })
          .then(r => r.json())
          .then(d => console.log('Publish hooks:', d.results))
          .catch(e => console.error('Publish hooks failed:', e))
        })
        onNavigate('blogs')
      }
    }
    setSaving(false)
  }

  const scoreColor = seo.score >= 80 ? 'var(--green)' : seo.score >= 50 ? 'var(--amber)' : 'var(--red)'
  const circumference = 2 * Math.PI * 20
  const dash = (seo.score / 100) * circumference
  const metaLen = (blog.meta_description || '').length
  const titleLen = (blog.meta_title || blog.title || '').length

  if (loading) return (
    <div className="page-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
      <span className="spinner" />
    </div>
  )

  return (
    <div className="page-content">
      <div className="editor-grid">

        {/* LEFT COLUMN */}
        <div className="editor-col">

          {/* AI Generator */}
          <div className="panel">
            <div className="panel-header">
              <span className="panel-title">AI Blog Generator</span>
              <span className="ai-label">✦ Claude AI</span>
            </div>
            <div className="panel-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                <div>
                  <label className="form-label">Focus Keyword</label>
                  <input className="form-input" placeholder="e.g. birdeye alternative" value={aiKeyword} onChange={e => setAiKeyword(e.target.value)} />
                </div>
                <div>
                  <label className="form-label">Topic Hint (optional)</label>
                  <input className="form-input" placeholder="e.g. for small businesses" value={aiTopic} onChange={e => setAiTopic(e.target.value)} />
                </div>
              </div>
              {generating && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'var(--accent-dim2)', borderRadius: 'var(--radius)', marginBottom: 12, fontSize: 13, color: 'var(--text2)' }}>
                  <span className="spinner" /> Generating your SEO blog post… (30–60 seconds)
                </div>
              )}
              <button className="btn btn-accent" onClick={generateWithAI} disabled={generating}>
                {generating ? <><span className="spinner" /> Generating…</> : <>✦ Generate Blog with AI</>}
              </button>
              <span style={{ fontSize: 11, color: 'var(--text3)', marginLeft: 12 }}>
                {blog.content_type === 'pillar'
                  ? '~3,000–4,000 words · Table of contents · 6–8 FAQs'
                  : blog.content_type === 'cluster'
                  ? '~1,200–1,500 words · Focused · Links to pillar'
                  : '~1,500–2,000 words · SEO optimised · FAQ included'}
              </span>
            </div>
          </div>

          {/* Content Organisation */}
          <div className="panel">
            <div className="panel-header">
              <span className="panel-title">Content Organisation</span>
            </div>
            <div className="panel-body">
              <div style={{ display: 'grid', gridTemplateColumns: blog.content_type === 'cluster' ? '1fr 1fr' : '1fr', gap: 10 }}>
                <div>
                  <label className="form-label">Content Type</label>
                  <select className="form-input" value={blog.content_type} onChange={e => update('content_type', e.target.value)}>
                    <option value="standalone">Standalone</option>
                    <option value="pillar">Pillar Page</option>
                    <option value="cluster">Cluster Post</option>
                  </select>
                </div>
                {blog.content_type === 'cluster' && (
                  <div>
                    <label className="form-label">Parent Pillar</label>
                    <select className="form-input" value={blog.pillar_id} onChange={e => update('pillar_id', e.target.value)}>
                      <option value="">— Select pillar —</option>
                      {pillars.map(p => (
                        <option key={p.id} value={p.id}>{p.title}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              {blog.content_type === 'cluster' && (
                <div style={{ marginTop: 10 }}>
                  <label className="form-label">Cluster Label <span style={{ color: 'var(--text3)', fontWeight: 400 }}>(optional)</span></label>
                  <input
                    className="form-input"
                    placeholder="e.g. GBP Optimisation"
                    value={blog.cluster_name || ''}
                    onChange={e => update('cluster_name', e.target.value)}
                  />
                </div>
              )}
              {blog.content_type === 'pillar' && (
                <p style={{ fontSize: 12, color: 'var(--text3)', marginTop: 10, marginBottom: 0 }}>
                  ✦ Pillar mode: AI will generate a 3,000–4,000 word comprehensive guide with table of contents and 6–8 FAQs.
                </p>
              )}
            </div>
          </div>

          {/* Post Details */}
          <div className="panel">
            <div className="panel-header"><span className="panel-title">Post Details</span></div>
            <div className="panel-body">
              <div className="form-row">
                <label className="form-label">Title</label>
                <input className="form-input input-title" placeholder="Your blog title…" value={blog.title} onChange={e => update('title', e.target.value)} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div className="form-row">
                  <label className="form-label">URL Slug</label>
                  <input className="form-input" placeholder="url-slug-here" value={blog.slug} onChange={e => update('slug', e.target.value)} />
                </div>
                <div className="form-row">
                  <label className="form-label">Focus Keyword</label>
                  <input className="form-input" placeholder="primary keyword" value={blog.focus_keyword} onChange={e => update('focus_keyword', e.target.value)} />
                </div>
              </div>
            </div>
          </div>

          {/* Content Editor */}
          <div className="panel">
            <div className="editor-toolbar">
              {[
                { key: 'h2', label: 'H2' }, { key: 'h3', label: 'H3' }, null,
                { key: 'bold', label: 'B' }, { key: 'italic', label: 'I' }, null,
                { key: 'link', label: '🔗' }, { key: 'ul', label: '• List' }, { key: 'ol', label: '1. List' }, null,
                { key: 'quote', label: '" Quote' }, { key: 'faq', label: '+ FAQ' }, { key: 'ilink', label: '⬡ Int. Link' },
              ].map((item, i) =>
                item === null
                  ? <div key={i} className="toolbar-sep" />
                  : <button key={item.key} className="toolbar-btn" onClick={() => toolbarAction(item.key)}>{item.label}</button>
              )}
            </div>
            <textarea
              id="main-editor"
              className="form-input"
              style={{ borderRadius: 0, border: 'none', borderTop: '1px solid var(--border)', minHeight: 480, padding: '18px', fontFamily: 'var(--font)', fontSize: 14, lineHeight: 1.85, resize: 'vertical', display: 'block', width: '100%' }}
              placeholder={"Start writing here…\n\nTip: Use the toolbar to format, or click 'Generate Blog with AI' above."}
              value={blog.content}
              onChange={e => update('content', e.target.value)}
            />
            <div style={{ padding: '10px 18px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text3)' }}>
              <span>{seo.words.toLocaleString()} words</span>
              <span style={{ color: seo.words >= 1500 ? 'var(--green)' : seo.words >= 800 ? 'var(--amber)' : 'var(--red)' }}>
                Target: 1,500–2,000 words
              </span>
            </div>
          </div>

          {/* Excerpt */}
          <div className="panel">
            <div className="panel-header"><span className="panel-title">Excerpt</span></div>
            <div className="panel-body">
              <textarea className="form-input" rows={3} placeholder="Short excerpt for blog listing…" value={blog.excerpt} onChange={e => update('excerpt', e.target.value)} />
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div className="editor-col">

          {/* Publish */}
          <div className="panel">
            <div className="panel-header"><span className="panel-title">Publish</span></div>
            <div className="panel-body" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button className="btn btn-accent btn-full" onClick={() => saveBlog('published')} disabled={saving}>
                {saving ? <><span className="spinner" /> Saving…</> : '🚀 Publish Now'}
              </button>
              <button className="btn btn-ghost btn-full" onClick={() => saveBlog('draft')} disabled={saving}>
                Save as Draft
              </button>
              <div className="form-row" style={{ marginTop: 8, marginBottom: 0 }}>
                <label className="form-label">Schedule for later</label>
                <input className="form-input" type="datetime-local" value={blog.scheduled_at} onChange={e => update('scheduled_at', e.target.value)} />
              </div>
              {blog.scheduled_at && (
                <button className="btn btn-ghost btn-full btn-sm" onClick={() => saveBlog('scheduled')} disabled={saving}>
                  Schedule Post
                </button>
              )}
            </div>
          </div>

          {/* SEO Score */}
          <div className="panel">
            <div className="seo-score-row">
              <div className="score-ring">
                <svg width="54" height="54" viewBox="0 0 54 54">
                  <circle cx="27" cy="27" r="20" fill="none" stroke="var(--bg4)" strokeWidth="4" />
                  <circle cx="27" cy="27" r="20" fill="none" stroke={scoreColor} strokeWidth="4"
                    strokeDasharray={`${dash} ${circumference}`} strokeLinecap="round"
                    style={{ transition: 'stroke-dasharray 0.4s' }} />
                </svg>
                <div className="score-ring-num" style={{ color: scoreColor }}>{seo.score}</div>
              </div>
              <div className="score-info">
                <div className="score-title">SEO Score</div>
                <div className="score-sub">{seo.score >= 80 ? 'Ready to publish' : seo.score >= 50 ? 'Needs improvement' : 'Needs work'}</div>
                <div className="progress-bar" style={{ marginTop: 4 }}>
                  <div className="progress-fill" style={{ width: `${seo.score}%`, background: scoreColor }} />
                </div>
              </div>
            </div>
            <div className="seo-checks">
              {seo.checks.map((c, i) => (
                <div className="seo-check" key={i}>
                  <div className={`check-dot ${c.pass ? 'pass' : c.warn ? 'warn' : 'fail'}`} />
                  {c.label}
                </div>
              ))}
            </div>
          </div>

          {/* SEO Meta */}
          <div className="panel">
            <div className="panel-header"><span className="panel-title">SEO Meta</span></div>
            <div className="panel-body">
              <div className="form-row">
                <label className="form-label">Meta Title</label>
                <input className="form-input" placeholder="SEO title (50–60 chars)" value={blog.meta_title} onChange={e => update('meta_title', e.target.value)} />
                <div className="input-footer">
                  <span className={`char-count ${titleLen > 60 ? 'over' : titleLen >= 50 ? '' : 'warn'}`}>{titleLen}/60</span>
                </div>
              </div>
              <div className="form-row">
                <label className="form-label">Meta Description</label>
                <textarea className="form-input" rows={3} placeholder="Meta description (140–155 chars)" value={blog.meta_description} onChange={e => update('meta_description', e.target.value)} />
                <div className="input-footer">
                  <span className={`char-count ${metaLen > 155 ? 'over' : metaLen >= 140 ? '' : 'warn'}`}>{metaLen}/155</span>
                </div>
              </div>
            </div>
          </div>

          {/* Settings */}
          <div className="panel">
            <div className="panel-header"><span className="panel-title">Settings</span></div>
            <div className="panel-body">
              <div className="form-row">
                <label className="form-label">Category</label>
                <select className="form-input" value={blog.category_id} onChange={e => update('category_id', e.target.value)}>
                  <option value="">Select category…</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="form-row">
                <label className="form-label">Tags (comma-separated)</label>
                <input className="form-input" placeholder="google reviews, local seo" value={Array.isArray(blog.tags) ? blog.tags.join(', ') : blog.tags} onChange={e => update('tags', e.target.value)} />
              </div>
              <div className="form-row">
                <label className="form-label">Featured Image</label>

                {/* Image Preview */}
                {blog.featured_image ? (
                  <div style={{ position: 'relative', marginBottom: 10 }}>
                    <img
                      src={blog.featured_image}
                      alt="Featured"
                      style={{ width: '100%', height: 140, objectFit: 'cover', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}
                      onError={e => { e.target.style.display = 'none' }}
                    />
                    <button
                      onClick={() => update('featured_image', '')}
                      style={{ position: 'absolute', top: 6, right: 6, background: 'rgba(0,0,0,0.7)', border: 'none', borderRadius: '50%', width: 24, height: 24, cursor: 'pointer', color: '#fff', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >✕</button>
                  </div>
                ) : (
                  <div style={{ width: '100%', height: 100, border: '2px dashed var(--border2)', borderRadius: 'var(--radius)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10, color: 'var(--text3)', fontSize: 12 }}>
                    No image selected
                  </div>
                )}

                {/* Upload Button */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <label style={{ flex: 1 }}>
                    <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageUpload} disabled={uploadingImage} />
                    <div className={`btn btn-ghost btn-sm btn-full ${uploadingImage ? '' : ''}`} style={{ cursor: 'pointer', textAlign: 'center', justifyContent: 'center' }}>
                      {uploadingImage ? <><span className="spinner" /> Uploading…</> : '↑ Upload Image'}
                    </div>
                  </label>
                </div>

                {/* Manual URL fallback */}
                <input
                  className="form-input"
                  placeholder="Or paste image URL…"
                  value={blog.featured_image}
                  onChange={e => update('featured_image', e.target.value)}
                  style={{ fontSize: 12 }}
                />
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>
                  Auto-fetched from Unsplash on AI generation · Upload to override · Max 5MB
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  )
}
